<template>
  <div
    class="flex flex-row items-center gap-2 justify-between h-11 border rounded-md group pr-2 pl-3"
    :class="{
      'border-brand bg-brand-primary_alt':
        localChecked && (!isEditMode || !isDeleteMode),
      'border-secondary bg-primary':
        !localChecked || isEditMode || isDeleteMode,
      'md:pl-1 md:pr-4': isEditMode,
      'md:pl-3 md:pr-4': !isEditMode && !isDeleteMode,
      'md:px-4': isDeleteMode,
    }"
  >
    <div class="flex flex-row items-center gap-3 grow truncate">
      <div v-if="!isEditMode && !isDeleteMode" class="pl-1 py-1">
        <Checkbox
          :checked="localChecked"
          :decorative="busy || disableCheckbox"
          :class="{
            'cursor-wait': busy,
            'cursor-not-allowed': disableCheckbox,
          }"
          :increasedTriggerArea="maxTablet"
          @update:checked="handleUpdateChecked"
        />
      </div>
      <div
        class="text-base md:text-sm font-medium text-primary flex grow truncate w-full"
        :class="{
          'line-through': localChecked && !(isEditMode || isDeleteMode),
        }"
        @click.stop="$emit('open:actions', $event)"
      >
        <TextInput
          v-if="isEditMode"
          ref="inputRef"
          v-model="description"
          @update:modelValue="$emit('update:description', $event)"
          class="text-sm w-full border-none bg-transparent focus:border-none focus:ring-transparent"
        />
        <TextWithTooltip
          v-else
          :title="item.description"
          :listeners="[width]"
          :lines="1"
        />
      </div>
      <template v-if="!isEditMode && !isDeleteMode">
        <div
          v-if="localChecked && item.checked_by"
          class="flex flex-row items-center gap-2 md:group-hover:hidden"
        >
          <Avatar
            :name="item.checked_by.fullname"
            :initials="item.checked_by.avatar_initials"
            :url="item.checked_by.avatar_url"
          />
        </div>
      </template>
    </div>
    <div
      class="flex-row items-center flex md:group-hover:flex"
      :class="!isEditMode && !isDeleteMode ? 'md:hidden' : 'md:flex'"
    >
      <slot name="actions" />
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watchEffect } from "vue";
import { debounce } from "vue-debounce";
import { Checkbox } from "@/SharedComponents/Checkboxes";
import { Avatar } from "@/SharedComponents/Avatars";
import { TextWithTooltip } from "@/SharedComponents/TextWithTooltip";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { TextInput } from "@/SharedComponents/Inputs";

const emit = defineEmits(["update:checked", "update:description"]);

const { width, maxTablet } = useBreakpoints();

const props = defineProps({
  item: { type: Object, required: true },
  isEditMode: { type: Boolean, required: false },
  isDeleteMode: { type: Boolean, required: false },
  canUpdateChecklist: { type: Boolean, required: true },
  currentUserId: { type: [Number, String], required: true },
  isAssignedToTask: { type: Boolean, required: true },
  isTaskCompleted: { type: Boolean, required: true },
  busy: { type: Boolean, required: false },
});

const inputRef = ref(null);
const description = ref(props.item.description);
const localChecked = ref(props.item.checked);
watchEffect(() => {
  localChecked.value = props.item.checked;
});
const debouncedEmit = debounce((value) => {
  emit("update:checked", value);
}, 200);
const handleUpdateChecked = (value) => {
  localChecked.value = value;
  debouncedEmit(value);
};

const disableCheckbox = computed(() => {
  // If task is completed, disable all interactions
  if (props.isTaskCompleted) {
    return true;
  }

  // If user has update permission, they can always check/uncheck
  if (props.canUpdateChecklist) {
    return false;
  }

  // If user is not assigned to task and current user is not the one who checked it,
  // they can't check/uncheck
  if (
    !props.isAssignedToTask &&
    props.item.checked_by?.id !== props.currentUserId
  ) {
    return true;
  }

  // If item is checked and current user is not the one who checked it,
  // they can't uncheck it
  if (props.item.checked && props.item.checked_by?.id !== props.currentUserId) {
    return true;
  }

  // Otherwise, users can check/uncheck
  return false;
});

watchEffect(() => {
  if (props.isEditMode) {
    description.value = props.item.description;
    nextTick(() => {
      inputRef.value.focus();
    });
  }
});
</script>
